import { datadogRum, RumInitConfiguration } from '@datadog/browser-rum'
import '@src/globals.css'
import '@src/SectionRenderer/sections/gridSlider/gridSlider.scss'
import '@src/quince-components/src/atoms/swiper/swiper.scss'
import '@src/quince-site-integrated/src/components/FormElements/inputStyles.scss'
import '@src/quince-site-integrated/src/components/FormElements/passwordStyles.scss'
import '@src/pages/contact-us/contact-us-components/glady-help-center/gladly-help-center-external.scss'
import '@src/quince-styles/scss/index.scss' // THIS IS INTENTIONAL DO NOT CHANGE THE ORDER!!!!! THIS SHOULD ALWAYS BE THE LAST IMPORT
import { AppProps } from 'next/app'
import Head from 'next/head'
import { appWithTranslation, useTranslation } from 'next-i18next'
import { useEffect } from 'react'

import { DD_RUM_APP_ID, DD_RUM_ENABLED_FLAG, DD_RUM_CLIENT_TOKEN, DD_RUM_SERVICE } from '@src/config/env'
import { initializeClientEntry } from '@src/modules/client-entry'
import { getIsNativeViewClient } from '@src/quince-site-common/src/helpers/getIsNativeViewClient'
import { useOnRouteUpdate } from '@src/quince-site-common/src/hooks/useOnRouteUpdate'
import { getCookie } from '@src/quince-site-integrated/src/utils'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { initializeFeCommon } from '@src/utils/feCommonInitialize'

import nextI18nextConfig from '../../next-i18next.config'

if (DD_RUM_ENABLED_FLAG && DD_RUM_APP_ID && DD_RUM_CLIENT_TOKEN) {
  const datadogConfig: RumInitConfiguration = {
    applicationId: DD_RUM_APP_ID,
    clientToken: DD_RUM_CLIENT_TOKEN,
    // `site` refers to the Datadog site parameter of your organization
    // see https://docs.datadoghq.com/getting_started/site/
    site: 'datadoghq.com',
    service: DD_RUM_SERVICE,
    env: process.env.NEXT_PUBLIC_DD_RUM_ENV,
    // Specify a version number to identify the deployed version of your application in Datadog
    version: process.env.NEXT_PUBLIC_RELEASE_VERSION,
    sessionSampleRate: 1,
    // All of those 1% sessions will include session replay
    sessionReplaySampleRate: 100,
    traceSampleRate: 1,
    telemetrySampleRate: 1,
    // @i18n-ignore
    defaultPrivacyLevel: 'mask-user-input',
    // Enable tracking of user interactions, resources, and long tasks (required for Clickmaps)
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    // TODO: Uncomment this when we have a way to trigger session-replay programmatically, Now Session Replay starts recording automatically when calling init()
    // startSessionReplayRecordingManually: true, // Allow to do session-replay programmatically if needed
    allowedTracingUrls: [
      // @i18n-ignore
      { match: 'https://api-prod-public.onequince.com/', propagatorTypes: ['tracecontext'] },
      // @i18n-ignore
      { match: 'https://api-qa.onequince.com/', propagatorTypes: ['tracecontext'] },
    ],
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  datadogRum.init(datadogConfig)
  // console.log('Datadog initialized with configuration', datadogConfig)
}
initializeFeCommon()
function MyApp({ Component, pageProps }: AppProps) {
  const { t } = useTranslation('common')
  useOnRouteUpdate()

  useEffect(() => {
    initializeClientEntry()
    const isNewUserInner = getCookie('new-user-created') === 'random' && getCookie('_lb_user_first_access') === 'true'

    if (isNewUserInner) {
      trackEvent(TELEMETRY_EVENT_NAMES.NEW_USER_CREATED_RANDOM, {})
    }
  }, [])

  return (
    <>
      <Head>
        <meta
          name="viewport"
          // @i18n-ignore
          content={`width=device-width, initial-scale=1${getIsNativeViewClient() ? ', maximum-scale=1' : ''}`}
        />
      </Head>
      <a className="sr-only" href="/accessibility">
        {t('view_our_accessibility_statement')}
      </a>
      <Component {...pageProps} />
    </>
  )
}

export default appWithTranslation(MyApp, nextI18nextConfig)
